import React, { createContext, useContext, useState, useEffect } from 'react';
import authService, { UserData } from '../services/authService';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserData | null;
  loading: boolean;
  isNewUser: boolean;
  setIsNewUser: (value: boolean) => void;
  hasCompletedSetup: boolean;
  setHasCompletedSetup: (value: boolean) => void;
  isAccountManager: boolean;
  refreshUserGroups: () => Promise<void>;
  signIn: (email: string, password: string, contextData?: any) => Promise<void>;
  signUp: (email: string, password: string, attributes: { [key: string]: string }) => Promise<any>;
  signOut: () => void;
  confirmSignUp: (email: string, code: string) => Promise<any>;
  resendConfirmationCode: (email: string) => Promise<any>;
  forgotPassword: (email: string) => Promise<any>;
  confirmPassword: (email: string, code: string, newPassword: string) => Promise<any>;
  getIdToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const [hasCompletedSetup, setHasCompletedSetup] = useState(false);
  const [isAccountManager, setIsAccountManager] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for cross-domain authentication tokens first
      const niwtAuthenticated = localStorage.getItem('niwt_authenticated') === 'true';
      const niwtAccessToken = localStorage.getItem('niwt_access_token');
      const niwtIdToken = localStorage.getItem('niwt_id_token');
      const niwtUserEmail = localStorage.getItem('niwt_user_email');
      
      if (niwtAuthenticated && niwtAccessToken && niwtIdToken) {
        // We have cross-domain auth tokens, use them
        const userAttributes = localStorage.getItem('niwt_user_attributes');
        const userData: UserData = userAttributes ? JSON.parse(userAttributes) : {
          email: niwtUserEmail || '',
          given_name: '',
          family_name: '',
          'custom:company': '',
          'custom:role': 'User',
          'custom:title': '',
          'custom:phone': ''
        };
        
        setUser(userData);
        setIsAuthenticated(true);
        
        // Check if user has completed Azure setup
        const setupStatus = localStorage.getItem(`azure_setup_${userData.email}`);
        setHasCompletedSetup(setupStatus === 'completed');
        
        setLoading(false);
        return;
      }
      
      // Fall back to checking native Cognito session
      if (authService.hasExistingSession()) {
        const initialized = await authService.initializeFromExistingSession();
        if (!initialized) {
          console.log('Could not initialize from existing session');
          setIsAuthenticated(false);
          setUser(null);
          setLoading(false);
          return;
        }
      }

      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        await authService.getSession();
        const userData = await authService.getUserData();
        setUser(userData);
        setIsAuthenticated(true);

        // Check if user has completed Azure setup
        const setupStatus = localStorage.getItem(`azure_setup_${userData.email}`);
        setHasCompletedSetup(setupStatus === 'completed');
      } else {
        // No current user - ensure we're not authenticated
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error: any) {
      // If Cognito is not properly configured, fail gracefully
      if (error.message && error.message.includes('User pool not initialized')) {
        console.warn('Cognito not configured properly, falling back to login');
      } else if (error.message && error.message.includes('Unable to load configuration for tenant')) {
        console.warn('Tenant configuration not found, falling back to login');
      } else {
        console.error('Auth check failed:', error);
      }
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string, contextData?: any) => {
    try {
      await authService.signIn(email, password, contextData);
      const userData = await authService.getUserData();
      setUser(userData);
      setIsAuthenticated(true);
      
      // Check if user has completed Azure setup (for existing users)
      const setupStatus = localStorage.getItem(`azure_setup_${userData.email}`);
      setHasCompletedSetup(setupStatus === 'completed');
    } catch (error) {
      throw error;
    }
  };

  const signUp = async (email: string, password: string, attributes: { [key: string]: string }) => {
    return authService.signUp(email, password, attributes);
  };

  const signOut = () => {
    try {
      // Clear cross-domain auth tokens
      localStorage.removeItem('niwt_authenticated');
      localStorage.removeItem('niwt_access_token');
      localStorage.removeItem('niwt_id_token');
      localStorage.removeItem('niwt_refresh_token');
      localStorage.removeItem('niwt_user_email');
      localStorage.removeItem('niwt_user_attributes');
      
      // Clear native Cognito session
      authService.signOut();
    } catch (error: any) {
      console.error('Sign out failed:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const confirmSignUp = async (email: string, code: string) => {
    const result = await authService.confirmSignUp(email, code);
    setIsNewUser(true);
    // New users haven't completed setup yet
    setHasCompletedSetup(false);
    return result;
  };

  const resendConfirmationCode = async (email: string) => {
    return authService.resendConfirmationCode(email);
  };

  const forgotPassword = async (email: string) => {
    return authService.forgotPassword(email);
  };

  const confirmPassword = async (email: string, code: string, newPassword: string) => {
    return authService.confirmPassword(email, code, newPassword);
  };

  const getIdToken = async () => {
    // Check for cross-domain token first
    const niwtIdToken = localStorage.getItem('niwt_id_token');
    if (niwtIdToken) {
      return niwtIdToken;
    }
    // Fall back to native Cognito token
    return authService.getIdToken();
  };

  const refreshUserGroups = async () => {
    try {
      const userData = await authService.getUserData();
      setUser(userData);
      // Check if user is an account manager (you can implement this logic based on your needs)
      // For now, setting to false - implement actual group checking logic
      setIsAccountManager(false);
    } catch (error: any) {
      console.error('Failed to refresh user groups:', error);
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    isNewUser,
    setIsNewUser,
    hasCompletedSetup,
    setHasCompletedSetup,
    isAccountManager,
    refreshUserGroups,
    signIn,
    signUp,
    signOut,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    confirmPassword,
    getIdToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};